import { IconArrowLeft } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { ActionOperationRecord } from "@/components/ai-operations/ActionOperationRecord";
import { ErrorState } from "@/components/shared/error-state";
import { Spinner } from "@/components/ui/spinner";
import { usePermission } from "@/hooks/use-permission";
import {
  cancelActionOperation,
  decideActionOperation,
  executeActionOperation,
  fetchActionOperationDetail,
  reconcileActionOperation,
} from "@/lib/ai-action-operation-api";
import { getApiErrorMessage } from "@/lib/api-error";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { grantedModulesOf, requireAnyModule } from "@/lib/module-access";
import { useAuthStore } from "@/stores/auth";

export const Route = createFileRoute("/_authed/ai-operations_/actions/$operationId")({
  validateSearch: z.object({ shopId: z.string().uuid().optional() }),
  beforeLoad: () => {
    requireAnyModule(["ai", "approvals"]);
  },
  component: ActionOperationRecordPage,
});

function ActionOperationRecordPage(): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { operationId } = Route.useParams();
  const selectedShopId = useAuthStore((state) => state.currentShopId);
  const shopId = Route.useSearch().shopId ?? selectedShopId;
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const user = useAuthStore((state) => state.user);
  const canViewApprovals = grantedModulesOf(user).includes("approvals");
  const detailQuery = useQuery({
    queryKey: ["ai-action-operation", shopId, operationId],
    queryFn: () => {
      if (shopId === null) throw new Error("Shop scope missing");
      return fetchActionOperationDetail(operationId, shopId);
    },
    enabled: shopId !== null,
  });
  const command = useMutation({
    mutationFn: async (action: "approve" | "reject" | "execute" | "cancel" | "reconcile") => {
      if (!shopId || !detailQuery.data) throw new Error("Operation scope missing");
      const input = { expectedVersion: detailQuery.data.version };
      if (action === "approve" || action === "reject")
        return decideActionOperation(operationId, shopId, { ...input, decision: action });
      if (action === "execute") return executeActionOperation(operationId, shopId, input);
      if (action === "cancel") return cancelActionOperation(operationId, shopId, input);
      return reconcileActionOperation(operationId, shopId, input);
    },
    onSuccess: () => toast.success(t("actionOperation.commands.saved")),
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["ai-action-operation", shopId, operationId],
      });
      await queryClient.invalidateQueries({ queryKey: ["campaign-ai-action-operations", shopId] });
    },
  });

  return (
    <main className="flex min-h-full min-w-0 flex-col gap-5 bg-canvas px-4 pb-8 pt-5 sm:px-6">
      <header className="flex flex-col gap-2 border-b border-line pb-4">
        <Link
          to={canViewApprovals ? "/ai-operations" : "/managed-groups"}
          search={canViewApprovals ? DEFAULT_APPROVAL_SEARCH : {}}
          className="inline-flex w-fit items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-ink"
        >
          <span className="grid size-4 place-items-center" aria-hidden>
            <IconArrowLeft size={14} />
          </span>
          {t(canViewApprovals ? "actionOperation.back" : "actionOperation.backToManagement")}
        </Link>
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">
            {t("actionOperation.recordTitle")}
          </h1>
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {operationId.slice(0, 8).toUpperCase()}
          </span>
        </div>
      </header>

      {shopId === null ? (
        <ErrorState message={t("actionOperation.shopRequired")} />
      ) : detailQuery.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted-foreground">
          <Spinner className="size-4" />
          <span>{t("common:state.loading")}</span>
        </div>
      ) : detailQuery.isError ? (
        <ErrorState
          message={t("actionOperation.loadError")}
          onRetry={() => void detailQuery.refetch()}
        />
      ) : detailQuery.data ? (
        <ActionOperationRecord
          detail={detailQuery.data}
          canApprove={hasPermission("approve")}
          canExecute={hasPermission("manage_ai") && hasPermission("approve")}
          canCancel={hasPermission("manage_ai")}
          busy={command.isPending}
          onDecision={(decision) => command.mutate(decision)}
          onExecute={() => command.mutate("execute")}
          onCancel={() => command.mutate("cancel")}
          onReconcile={() => command.mutate("reconcile")}
          onRefresh={() => void detailQuery.refetch()}
        />
      ) : null}
    </main>
  );
}
